/**
 * Prop Bet Resolver
 * Handles resolution of all prop bet types based on fight results
 */

import type { Bet, Fight, FightResult } from '../types';
import { calculatePayout } from './betting';

export interface BetResolution {
  status: 'won' | 'lost' | 'push';
  payout?: number;
  refund?: number; // For pushes and no contest
}


/**
 * Parse total rounds from outcome name (e.g., "Over 2.5" -> 2.5)
 */
function parseTotalRounds(outcomeName: string): number | null {
  const match = outcomeName.match(/(?:Over|Under)\s+(\d+\.?\d*)/i);
  if (match) {
    return parseFloat(match[1]);
  }
  return null;
}

/**
 * Check if outcome is Over or Under
 */
function isOverBet(outcomeName: string): boolean {
  return outcomeName.toLowerCase().includes('over');
}

/**
 * Check if method matches outcome name
 * Handles various method formats
 */
function methodMatches(outcomeName: string, method: FightResult['method']): boolean {
  const outcomeLower = outcomeName.toLowerCase();
  
  // Check if outcome contains method keywords
  if (method === 'ko_tko') {
    return outcomeLower.includes('ko') || outcomeLower.includes('tko') || outcomeLower.includes('knockout');
  }
  if (method === 'submission') {
    return outcomeLower.includes('submission') || outcomeLower.includes('sub');
  }
  if (method === 'decision_unanimous') {
    return outcomeLower.includes('unanimous');
  }
  if (method === 'decision_majority') {
    return outcomeLower.includes('majority');
  }
  if (method === 'decision_split') {
    return outcomeLower.includes('split');
  }
  if (method === 'dq') {
    return outcomeLower.includes('dq') || outcomeLower.includes('disqualification');
  }

  // Check for "inside distance" (KO/TKO/Submission/DQ)
  if (outcomeLower.includes('inside distance')) {
    if (method === 'draw' || method === 'no_contest') return false;
    return method === 'ko_tko' || method === 'submission' || method === 'dq';
  }

  // Check for "decision" (any decision type)
  if (outcomeLower.includes('decision') && !outcomeLower.includes('unanimous') && !outcomeLower.includes('majority') && !outcomeLower.includes('split')) {
    if (method === 'draw' || method === 'no_contest') return false;
    return method === 'decision_unanimous' || method === 'decision_majority' || method === 'decision_split';
  }

  // Check for "by points" (any decision)
  if (outcomeLower.includes('by points') || outcomeLower.includes('by decision')) {
    if (method === 'draw' || method === 'no_contest') return false;
    return method === 'decision_unanimous' || method === 'decision_majority' || method === 'decision_split';
  }

  return false;
}

/**
 * Parse fighter name from outcome name
 */
function parseFighterName(outcomeName: string, fighters: Fight['fighters']): string | null {
  for (const fighter of fighters) {
    if (outcomeName.includes(fighter.name)) {
      return fighter.id;
    }
  }
  return null;
}

/**
 * Parse round number from outcome name (e.g., "Round 1" -> 1)
 */
function parseRound(outcomeName: string): number | null {
  const match = outcomeName.match(/round\s*(\d+)/i);
  if (match) {
    return parseInt(match[1], 10);
  }
  return null;
}

/**
 * Parse seconds from outcome name for squares (e.g., "00" -> 0, "53" -> 53)
 */
function parseSquares(outcomeName: string): number | null {
  const match = outcomeName.match(/^(\d{2})$/);
  if (match) {
    return parseInt(match[1], 10);
  }
  return null;
}

/**
 * Parse winning minute from outcome name (e.g., "1st Minute of Round 1" -> {minute: 1, round: 1})
 */
function parseWinningMinute(outcomeName: string): { minute: number; round: number } | null {
  const match = outcomeName.match(/(\d+)(?:st|nd|rd|th)\s+Minute\s+of\s+Round\s+(\d+)/i);
  if (match) {
    return {
      minute: parseInt(match[1], 10),
      round: parseInt(match[2], 10),
    };
  }
  return null;
}

/**
 * Parse fight winner and total rounds (e.g., "Fighter & Over 2.5 Rounds")
 */
function parseFighterAndTotalRounds(outcomeName: string, fighters: Fight['fighters']): { fighterId: string | null; isOver: boolean; rounds: number } | null {
  // Parse fighter
  let fighterId: string | null = null;
  for (const fighter of fighters) {
    if (outcomeName.includes(fighter.name)) {
      fighterId = fighter.id;
      break;
    }
  }
  
  // Parse Over/Under and rounds
  const overMatch = outcomeName.match(/&\s*Over\s+(\d+\.?\d*)/i);
  const underMatch = outcomeName.match(/&\s*Under\s+(\d+\.?\d*)/i);
  
  if (overMatch) {
    return {
      fighterId,
      isOver: true,
      rounds: parseFloat(overMatch[1]),
    };
  }
  
  if (underMatch) {
    return {
      fighterId,
      isOver: false,
      rounds: parseFloat(underMatch[1]),
    };
  }
  
  return null;
}

/**
 * Check if outcome name contains "OR" (combination bet)
 */
function hasOrClause(outcomeName: string): boolean {
  return outcomeName.toLowerCase().includes(' or ');
}

/**
 * Split outcome name by OR to check multiple conditions
 */
function splitOrClause(outcomeName: string): string[] {
  return outcomeName.split(/ or /i).map(s => s.trim());
}

/**
 * Type guard to check if method is draw or no contest
 */
function isDrawOrNoContest(method: FightResult['method']): method is 'draw' | 'no_contest' {
  return method === 'draw' || method === 'no_contest';
}

/**
 * Get total seconds into the fight from result
 */
function getTotalSecondsIntoFight(result: FightResult): number | null {
  if (!result.round || !result.timeInRound) return null;
  
  const [minutes, seconds] = result.timeInRound.split(':').map(Number);
  const roundMinutes = (result.round - 1) * 5;
  return roundMinutes * 60 + minutes * 60 + seconds;
}

/**
 * Check if fight ended in specific minute of any round
 * e.g., "4th Minute" = 3:00-3:59 of any round
 */
function checkMinuteOfAnyRound(outcomeName: string, result: FightResult): boolean {
  if (result.wentTheDistance || !result.timeInRound) return false;
  
  const match = outcomeName.match(/(\d+)(?:st|nd|rd|th)\s+Minute/i);
  if (!match) return false;
  
  const targetMinute = parseInt(match[1], 10);
  const [minutes] = result.timeInRound.split(':').map(Number);
  const actualMinute = minutes + 1; // 0:xx is "1st minute", 1:xx is "2nd minute"
  
  return actualMinute === targetMinute;
}

/**
 * Check if fighter won in first X minutes of the fight
 * e.g., "in the 1st 3 Minutes of the Fight" = before 3:00 of R1
 */
function checkFirstMinutesOfFight(outcomeName: string, fighterName: string | null, result: FightResult, fighters: Fight['fighters']): boolean {
  if (result.wentTheDistance) return false;
  
  // Check if specific fighter or "Either Fighter"
  if (fighterName) {
    const fighterId = parseFighterName(fighterName, fighters);
    if (fighterId !== result.winnerId) return false;
  }
  
  const match = outcomeName.match(/(?:1st|first)\s+(\d+)\s+Minutes?\s+of\s+(?:the\s+)?Fight/i);
  if (!match) return false;
  
  const minutes = parseInt(match[1], 10);
  const totalSeconds = getTotalSecondsIntoFight(result);
  if (totalSeconds === null) return false;
  
  return totalSeconds < (minutes * 60);
}

/**
 * Check if fight ended in last X minutes of round 5
 * e.g., "Last 2 Minutes of the 5th Round" = after 3:00 of R5
 */
function checkLastMinutesOfRound5(outcomeName: string, result: FightResult): boolean {
  if (result.wentTheDistance || result.round !== 5 || !result.timeInRound) return false;
  
  const match = outcomeName.match(/Last\s+(\d+)\s+Minutes?\s+of\s+(?:the\s+)?5th\s+Round/i);
  if (!match) return false;
  
  const minutes = parseInt(match[1], 10);
  const [mins] = result.timeInRound.split(':').map(Number);
  
  // e.g., Last 2 minutes = after 3:00 (minutes >= 3)
  return mins >= (5 - minutes);
}

/**
 * Check "Fighter to Win & Round X to Start"
 * Fighter must win AND fight must reach round X (start of round X)
 */
function checkFighterAndRoundStart(outcomeName: string, result: FightResult, fighters: Fight['fighters']): boolean {
  const match = outcomeName.match(/(.+?)\s+to\s+Win\s+&\s+Round\s+(\d+)\s+to\s+Start/i);
  if (!match) return false;
  
  const fighterName = match[1].trim();
  const targetRound = parseInt(match[2], 10);
  
  const fighterId = parseFighterName(fighterName, fighters);
  if (fighterId !== result.winnerId) return false;
  
  // Fight must have reached the target round (i.e., ended in that round or later)
  if (!result.round) return false;
  return result.round >= targetRound;
}

/**
 * Check "Either Fighter to Win in Rounds X or Y"
 */
function checkEitherFighterInRounds(outcomeName: string, result: FightResult): boolean {
  if (result.wentTheDistance || !result.round) return false;
  
  const match = outcomeName.match(/Either\s+Fighter\s+to\s+Win\s+in\s+Rounds?\s+(\d+)\s+or\s+(\d+)/i);
  if (!match) return false;
  
  const round1 = parseInt(match[1], 10);
  const round2 = parseInt(match[2], 10);
  
  return result.round === round1 || result.round === round2;
}

/**
 * Check "Either Fighter to Win in the 1st X Seconds of the Fight"
 */
function checkFirstSecondsOfFight(outcomeName: string, result: FightResult): boolean {
  if (result.wentTheDistance) return false;
  
  const match = outcomeName.match(/(?:1st|first)\s+(\d+)\s+Seconds?\s+of\s+(?:the\s+)?Fight/i);
  if (!match) return false;
  
  const seconds = parseInt(match[1], 10);
  const totalSeconds = getTotalSecondsIntoFight(result);
  if (totalSeconds === null) return false;
  
  return totalSeconds < seconds;
}

/**
 * Check "Either Fighter to Win in the Last X Seconds of the 5th Round"
 */
function checkLastSecondsOfRound5(outcomeName: string, result: FightResult): boolean {
  if (result.wentTheDistance || result.round !== 5 || !result.timeInRound) return false;
  
  const match = outcomeName.match(/Last\s+(\d+)\s+Seconds?\s+of\s+(?:the\s+)?5th\s+Round/i);
  if (!match) return false;
  
  const seconds = parseInt(match[1], 10);
  const [minutes, secs] = result.timeInRound.split(':').map(Number);
  const totalSecondsInRound = minutes * 60 + secs;
  
  // e.g., Last 30 seconds = after 4:30 (270 seconds)
  return totalSecondsInRound >= (300 - seconds);
}

/**
 * Check "Either Fighter to Win by Method in the 1st X Minutes of Round 1"
 */
function checkEitherFighterByMethodInFirstMinutes(outcomeName: string, result: FightResult): boolean {
  if (result.wentTheDistance || result.round !== 1 || !result.timeInRound) return false;
  if (isDrawOrNoContest(result.method)) return false;
  
  const match = outcomeName.match(/Either\s+Fighter\s+to\s+Win\s+by\s+(.+?)\s+in\s+(?:the\s+)?(?:1st|first)\s+(\d+)\s+Minutes?\s+of\s+Round\s+1/i);
  if (!match) return false;
  
  const methodStr = match[1].trim();
  const minutes = parseInt(match[2], 10);
  
  // Check method matches
  if (!methodMatches(methodStr, result.method)) return false;
  
  // Check time
  const totalSeconds = getTotalSecondsIntoFight(result);
  if (totalSeconds === null) return false;
  
  return totalSeconds < (minutes * 60);
}

/**
 * Check "Either Fighter to Win by Method in the 1st X Seconds of Round 1"
 */
function checkEitherFighterByMethodInFirstSeconds(outcomeName: string, result: FightResult): boolean {
  if (result.wentTheDistance || result.round !== 1 || !result.timeInRound) return false;
  if (isDrawOrNoContest(result.method)) return false;
  
  const match = outcomeName.match(/Either\s+Fighter\s+to\s+Win\s+by\s+(.+?)\s+in\s+(?:the\s+)?(?:1st|first)\s+(\d+)\s+Seconds?\s+of\s+Round\s+1/i);
  if (!match) return false;
  
  const methodStr = match[1].trim();
  const seconds = parseInt(match[2], 10);
  
  // Check method matches
  if (!methodMatches(methodStr, result.method)) return false;
  
  // Check time
  const totalSeconds = getTotalSecondsIntoFight(result);
  if (totalSeconds === null) return false;
  
  return totalSeconds < seconds;
}

/**
 * Check "Fighter to Win by Method in the 1st X Seconds of Round 1"
 */
function checkSpecificFighterByMethodInFirstSeconds(outcomeName: string, result: FightResult, fighters: Fight['fighters']): boolean {
  if (result.wentTheDistance || result.round !== 1 || !result.timeInRound) return false;
  if (isDrawOrNoContest(result.method)) return false;
  
  const match = outcomeName.match(/(.+?)\s+to\s+Win\s+by\s+(.+?)\s+in\s+(?:the\s+)?(?:1st|first)\s+(\d+)\s+seconds?\s+of\s+Round\s+1/i);
  if (!match) return false;
  
  const fighterName = match[1].trim();
  const methodStr = match[2].trim();
  const seconds = parseInt(match[3], 10);
  
  // Check fighter
  const fighterId = parseFighterName(fighterName, fighters);
  if (fighterId !== result.winnerId) return false;
  
  // Check method
  if (!methodMatches(methodStr, result.method)) return false;
  
  // Check time
  const totalSeconds = getTotalSecondsIntoFight(result);
  if (totalSeconds === null) return false;
  
  return totalSeconds < seconds;
}

/**
 * Check "Fighter by Method or Fighter by Method" (complex OR)
 */
function checkComplexOrConditions(outcomeName: string, result: FightResult, fighters: Fight['fighters']): boolean {
  if (isDrawOrNoContest(result.method)) return false;
  
  // Split by "or"
  const clauses = outcomeName.split(/\s+or\s+/i);
  
  for (const clause of clauses) {
    const trimmedClause = clause.trim();
    
    // Try to match "Fighter by Method" pattern
    const fighterMethodMatch = trimmedClause.match(/(.+?)\s+by\s+(.+)/i);
    if (fighterMethodMatch) {
      const fighterName = fighterMethodMatch[1].trim();
      const methodStr = fighterMethodMatch[2].trim();
      
      const fighterId = parseFighterName(fighterName, fighters);
      if (fighterId === result.winnerId && methodMatches(methodStr, result.method)) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Resolve a single bet based on fight result
 */
export function resolvePropBet(bet: Bet, fight: Fight, result: FightResult): BetResolution {
  // Normalize marketKey to lowercase for consistent comparison
  const marketKey = bet.marketKey?.toLowerCase().replace(/\s+/g, '_') || '';
  
  // No Contest - refund all bets
  if (result.method === 'no_contest') {
    return {
      status: 'push',
      refund: bet.amount, // Refund counts as winnings
    };
  }

  // Moneyline bet - check fighterId directly OR parse from outcomeName
  // This handles both regular bets and parlay legs
  if (bet.fighterId && (!marketKey || marketKey === 'moneyline')) {
    if (bet.fighterId === result.winnerId) {
      const payout = calculatePayout(bet.amount, bet.odds);
      return {
        status: 'won',
        payout,
      };
    }
    return { status: 'lost' };
  }

  // Prop bet (has marketKey and outcomeName)
  if (marketKey && bet.outcomeName) {
    const outcomeName = bet.outcomeName;

    // Moneyline market (fighter pick) - parse from outcomeName if fighterId not set
    if (marketKey === 'moneyline') {
      const fighterId = parseFighterName(outcomeName, fight.fighters);
      if (fighterId === result.winnerId) {
        const payout = calculatePayout(bet.amount, bet.odds);
        return {
          status: 'won',
          payout,
        };
      }
      return { status: 'lost' };
    }

    // Total Rounds Over/Under
    if (marketKey === 'total_rounds') {
      const total = parseTotalRounds(outcomeName);
      if (total === null) return { status: 'lost' };

      const isOver = isOverBet(outcomeName);
      
      // If fight went the distance, it's 25 minutes (5 rounds)
      if (result.wentTheDistance) {
        if (isOver) {
          // Over always wins if fight went distance
          const payout = calculatePayout(bet.amount, bet.odds);
          return { status: 'won', payout };
        } else {
          // Under loses if fight went distance
          return { status: 'lost' };
        }
      }

      // Calculate exact time in minutes
      if (!result.round || !result.timeInRound) {
        return { status: 'lost' };
      }

      const [minutes, seconds] = result.timeInRound.split(':').map(Number);
      const roundMinutes = (result.round - 1) * 5;
      const totalSeconds = roundMinutes * 60 + minutes * 60 + seconds;
      
      // Calculate threshold in seconds (e.g., 2.5 rounds = 2:30 = 150 seconds)
      const thresholdSeconds = total * 60;

      if (totalSeconds > thresholdSeconds) {
        // Over wins
        if (isOver) {
          const payout = calculatePayout(bet.amount, bet.odds);
          return { status: 'won', payout };
        }
        return { status: 'lost' };
      } else if (totalSeconds < thresholdSeconds) {
        // Under wins
        if (!isOver) {
          const payout = calculatePayout(bet.amount, bet.odds);
          return { status: 'won', payout };
        }
        return { status: 'lost' };
      } else {
        // Push (exact match) - refund
        return {
          status: 'push',
          refund: bet.amount,
        };
      }
    }

    // Method of Victory
    if (marketKey === 'method_of_victory') {
      // Skip draw/no_contest for method bets (early return)
      if (isDrawOrNoContest(result.method)) {
        return { status: 'lost' };
      }

      // Type narrowing - we know method is not draw/no_contest
      const method = result.method;

      // Check for complex OR bets (e.g., "Fighter 1 by KO OR Fighter 2 by Decision")
      if (hasOrClause(outcomeName)) {
        const clauses = splitOrClause(outcomeName);
        for (const clause of clauses) {
          // Parse fighter from this clause
          const fighterId = parseFighterName(clause.trim(), fight.fighters);
          
          // Check if this clause matches: correct winner AND correct method
          if (fighterId === result.winnerId && methodMatches(clause.trim(), method)) {
            const payout = calculatePayout(bet.amount, bet.odds);
            return { status: 'won', payout };
          }
        }
        return { status: 'lost' };
      }

      // Check for "inside distance" (multiple methods)
      if (outcomeName.toLowerCase().includes('inside distance')) {
        const fighterId = parseFighterName(outcomeName, fight.fighters);
        if (fighterId === result.winnerId) {
          const isInsideDistance = method === 'ko_tko' || method === 'submission' || method === 'dq';
          if (isInsideDistance) {
            const payout = calculatePayout(bet.amount, bet.odds);
            return { status: 'won', payout };
          }
        }
        return { status: 'lost' };
      }

      // Check for "by decision" or "by points" (any decision)
      if (outcomeName.toLowerCase().includes('by decision') || outcomeName.toLowerCase().includes('by points')) {
        const fighterId = parseFighterName(outcomeName, fight.fighters);
        if (fighterId === result.winnerId) {
          const isDecision = method === 'decision_unanimous' || method === 'decision_majority' || method === 'decision_split';
          if (isDecision) {
            const payout = calculatePayout(bet.amount, bet.odds);
            return { status: 'won', payout };
          }
        }
        return { status: 'lost' };
      }

      // Check for double chance (e.g., "by TKO/KO/DQ or Submission")
      if (outcomeName.toLowerCase().includes(' or ')) {
        const fighterId = parseFighterName(outcomeName, fight.fighters);
        if (fighterId === result.winnerId) {
          // Check if method matches any part of the OR clause
          if (methodMatches(outcomeName, method)) {
            const payout = calculatePayout(bet.amount, bet.odds);
            return { status: 'won', payout };
          }
        }
        return { status: 'lost' };
      }

      // Standard method check (fighter + method)
      const fighterId = parseFighterName(outcomeName, fight.fighters);
      if (fighterId === result.winnerId) {
        if (methodMatches(outcomeName, method)) {
          const payout = calculatePayout(bet.amount, bet.odds);
          return { status: 'won', payout };
        }
      }
      return { status: 'lost' };
    }

    // How Will Fight End (no fighter, just method)
    if (marketKey === 'how_will_fight_end') {
      // Skip draw/no_contest for method bets
      if (isDrawOrNoContest(result.method)) {
        return { status: 'lost' };
      }
      // Type narrowing - we know method is not draw/no_contest
      const method = result.method;
      if (methodMatches(outcomeName, method)) {
        const payout = calculatePayout(bet.amount, bet.odds);
        return { status: 'won', payout };
      }
      return { status: 'lost' };
    }

    // Round Props (fighter wins in specific round OR by points)
    if (marketKey === 'round_props') {
      // Check for "by points" (decision)
      if (outcomeName.toLowerCase().includes('by points')) {
        const fighterId = parseFighterName(outcomeName, fight.fighters);
        if (fighterId === result.winnerId && result.wentTheDistance) {
          const payout = calculatePayout(bet.amount, bet.odds);
          return { status: 'won', payout };
        }
        return { status: 'lost' };
      }

      // Check for specific round (fighter wins in round X)
      const round = parseRound(outcomeName);
      const fighterId = parseFighterName(outcomeName, fight.fighters);
      
      if (round !== null && fighterId !== null) {
        if (fighterId === result.winnerId && result.round === round && !result.wentTheDistance) {
          const payout = calculatePayout(bet.amount, bet.odds);
          return { status: 'won', payout };
        }
        return { status: 'lost' };
      }

      // Check for "When Will The Fight End" (just round, no fighter)
      if (round !== null && fighterId === null) {
        if (result.round === round && !result.wentTheDistance) {
          const payout = calculatePayout(bet.amount, bet.odds);
          return { status: 'won', payout };
        }
        return { status: 'lost' };
      }

      return { status: 'lost' };
    }

    // Fight Props (go distance, complete rounds, inside distance only)
    if (marketKey === 'fight_props') {
      // "Will Fight Go The Distance"
      if (outcomeName.toLowerCase().includes('go distance')) {
        const isYes = outcomeName.toLowerCase().includes('yes');
        if (result.wentTheDistance === isYes) {
          const payout = calculatePayout(bet.amount, bet.odds);
          return { status: 'won', payout };
        }
        return { status: 'lost' };
      }
      
      // "Fight Winner - Inside The Distance Only" (fighter wins, NOT by decision)
      if (outcomeName.toLowerCase().includes('inside distance only')) {
        // Parse fighter from outcome
        const fighterId = parseFighterName(outcomeName, fight.fighters);
        
        // If fight went to decision, bet is void/push
        if (result.wentTheDistance) {
          return { status: 'push', refund: bet.amount };
        }
        
        // Check if correct fighter won
        if (fighterId === result.winnerId) {
          const payout = calculatePayout(bet.amount, bet.odds);
          return { status: 'won', payout };
        }
        return { status: 'lost' };
      }

      // "Fight To Complete X Full Rounds"
      const completeMatch = outcomeName.match(/complete\s*(\d+)\s*round/i);
      if (completeMatch) {
        const requiredRound = parseInt(completeMatch[1], 10);
        const isYes = outcomeName.toLowerCase().includes('yes');
        
        if (result.wentTheDistance) {
          // Fight went distance, so all rounds completed
          if (isYes && requiredRound <= 5) {
            const payout = calculatePayout(bet.amount, bet.odds);
            return { status: 'won', payout };
          }
          return { status: 'lost' };
        }

        // Fight didn't go distance
        if (!result.round) {
          return { status: 'lost' };
        }

        const roundCompleted = result.round >= requiredRound;
        
        if (isYes && roundCompleted) {
          const payout = calculatePayout(bet.amount, bet.odds);
          return { status: 'won', payout };
        } else if (!isYes && !roundCompleted) {
          const payout = calculatePayout(bet.amount, bet.odds);
          return { status: 'won', payout };
        }
        return { status: 'lost' };
      }

      return { status: 'lost' };
    }

    // Squares (last two digits of finishing time)
    if (marketKey === 'squares_game' || outcomeName.match(/^\d{2}$/)) {
      const squares = parseSquares(outcomeName);
      if (squares === null) return { status: 'lost' };

      // Fight must end inside distance (not by decision)
      if (result.wentTheDistance) return { status: 'lost' };
      if (!result.timeInRound) return { status: 'lost' };

      // Get last two digits (the seconds part)
      const [, seconds] = result.timeInRound.split(':').map(Number);
      
      if (seconds === squares) {
        const payout = calculatePayout(bet.amount, bet.odds);
        return { status: 'won', payout };
      }
      return { status: 'lost' };
    }
    
    // Winning Minute
    if (marketKey === 'winning_minute') {
      const winningMinute = parseWinningMinute(outcomeName);
      if (!winningMinute) return { status: 'lost' };
      
      // Fight must end inside distance
      if (result.wentTheDistance) return { status: 'lost' };
      if (!result.round || !result.timeInRound) return { status: 'lost' };
      
      // Check if round matches
      if (result.round !== winningMinute.round) return { status: 'lost' };
      
      // Parse time to get which minute it ended in
      // 1st minute = 0:00-0:59, 2nd minute = 1:00-1:59, etc.
      const [minutes] = result.timeInRound.split(':').map(Number);
      const actualMinute = minutes + 1; // 0:xx is "1st minute", 1:xx is "2nd minute"
      
      if (actualMinute === winningMinute.minute) {
        const payout = calculatePayout(bet.amount, bet.odds);
        return { status: 'won', payout };
      }
      return { status: 'lost' };
    }
    
    // Fight Winner & Total Rounds (both conditions must be met)
    if (marketKey === 'fighter_and_total_rounds') {
      const parsed = parseFighterAndTotalRounds(outcomeName, fight.fighters);
      if (!parsed) return { status: 'lost' };
      
      // Check winner condition
      if (parsed.fighterId !== result.winnerId) return { status: 'lost' };
      
      // Check total rounds condition
      const totalRoundsBet = parsed.isOver ? 'Over' : 'Under';
      const totalRoundsOutcome = `${totalRoundsBet} ${parsed.rounds}`;
      
      // Create a temporary bet to check the total rounds condition
      const tempBet: Bet = {
        ...bet,
        marketKey: 'total_rounds',
        outcomeName: totalRoundsOutcome,
      };
      
      const totalRoundsResult = resolvePropBet(tempBet, fight, result);
      
      // Both conditions must be true
      if (totalRoundsResult.status === 'won') {
        const payout = calculatePayout(bet.amount, bet.odds);
        return { status: 'won', payout };
      }
      
      return { status: 'lost' };
    }

    // Draw/No Contest
    if (marketKey === 'draw_no_contest') {
      if (isDrawOrNoContest(result.method)) {
        // Refund for draw/no contest
        return { status: 'push', refund: bet.amount };
      }
      return { status: 'lost' };
    }
    
    // Bout Specials (complex multi-condition bets)
    if (marketKey === 'bout_specials') {
      let won = false;
      
      // 1. "Fighter to Win & Round X to Start"
      if (outcomeName.match(/to\s+Win\s+&\s+Round\s+\d+\s+to\s+Start/i)) {
        won = checkFighterAndRoundStart(outcomeName, result, fight.fighters);
      }
      // 2. "Fighter by Method or Fighter by Method" (complex OR with multiple fighters and methods)
      else if (outcomeName.match(/by\s+.+\s+or\s+.+\s+by\s+/i)) {
        won = checkComplexOrConditions(outcomeName, result, fight.fighters);
      }
      // 3. "Either Fighter to Win in Rounds X or Y"
      else if (outcomeName.match(/Either\s+Fighter\s+to\s+Win\s+in\s+Rounds?\s+\d+\s+or\s+\d+/i)) {
        won = checkEitherFighterInRounds(outcomeName, result);
      }
      // 4. "Fight to be Won in the Xth Minute of any Round"
      else if (outcomeName.match(/(?:Fight\s+to\s+be\s+Won|Won)\s+in\s+the\s+\d+(?:st|nd|rd|th)\s+Minute\s+of\s+any\s+Round/i)) {
        won = checkMinuteOfAnyRound(outcomeName, result);
      }
      // 5. "Either Fighter to Win by Method in the 1st X Minutes of Round 1"
      else if (outcomeName.match(/Either\s+Fighter\s+to\s+Win\s+by\s+.+\s+in\s+(?:the\s+)?(?:1st|first)\s+\d+\s+Minutes?\s+of\s+Round\s+1/i)) {
        won = checkEitherFighterByMethodInFirstMinutes(outcomeName, result);
      }
      // 6. "Either Fighter to Win by Method in the 1st X Seconds of Round 1"
      else if (outcomeName.match(/Either\s+Fighter\s+to\s+Win\s+by\s+.+\s+in\s+(?:the\s+)?(?:1st|first)\s+\d+\s+Seconds?\s+of\s+Round\s+1/i)) {
        won = checkEitherFighterByMethodInFirstSeconds(outcomeName, result);
      }
      // 7. "Fighter to Win by Method in the 1st X Seconds of Round 1"
      else if (outcomeName.match(/.+\s+to\s+Win\s+by\s+.+\s+in\s+(?:the\s+)?(?:1st|first)\s+\d+\s+seconds?\s+of\s+Round\s+1/i)) {
        won = checkSpecificFighterByMethodInFirstSeconds(outcomeName, result, fight.fighters);
      }
      // 8. "Either Fighter to Win in the 1st X Minutes of the Fight"
      else if (outcomeName.match(/Either\s+Fighter\s+to\s+Win\s+in\s+(?:the\s+)?(?:1st|first)\s+\d+\s+Minutes?\s+of\s+(?:the\s+)?Fight/i)) {
        won = checkFirstMinutesOfFight(outcomeName, null, result, fight.fighters);
      }
      // 9. "Fighter to Win in the 1st X Minutes of the Fight"
      else if (outcomeName.match(/.+\s+to\s+Win\s+in\s+(?:the\s+)?(?:1st|first)\s+\d+\s+Minutes?\s+of\s+(?:the\s+)?Fight/i)) {
        // Extract fighter name
        const fighterMatch = outcomeName.match(/(.+?)\s+to\s+Win\s+in/i);
        const fighterName = fighterMatch ? fighterMatch[1].trim() : null;
        won = checkFirstMinutesOfFight(outcomeName, fighterName, result, fight.fighters);
      }
      // 10. "Either Fighter to Win in the 1st X Seconds of the Fight"
      else if (outcomeName.match(/Either\s+Fighter\s+to\s+Win\s+in\s+(?:the\s+)?(?:1st|first)\s+\d+\s+Seconds?\s+of\s+(?:the\s+)?Fight/i)) {
        won = checkFirstSecondsOfFight(outcomeName, result);
      }
      // 11. "Either Fighter to Win in the Last X Minutes of the 5th Round"
      else if (outcomeName.match(/Either\s+Fighter\s+to\s+Win\s+in\s+(?:the\s+)?Last\s+\d+\s+Minutes?\s+of\s+(?:the\s+)?5th\s+Round/i)) {
        won = checkLastMinutesOfRound5(outcomeName, result);
      }
      // 12. "Either Fighter to Win in the Last X Seconds of the 5th Round"
      else if (outcomeName.match(/Either\s+Fighter\s+to\s+Win\s+in\s+(?:the\s+)?Last\s+\d+\s+Seconds?\s+of\s+(?:the\s+)?5th\s+Round/i)) {
        won = checkLastSecondsOfRound5(outcomeName, result);
      }
      // 13. "Fight to be Won in the 1st Minute of Round 1 or the Last Minute of the 5th Round" (multi-condition OR)
      else if (outcomeName.toLowerCase().includes(' or ')) {
        // Split by OR and check each condition
        const clauses = outcomeName.split(/\s+or\s+/i);
        for (const clause of clauses) {
          const trimmedClause = clause.trim();
          
          // Check various patterns for each clause
          if (trimmedClause.match(/\d+(?:st|nd|rd|th)\s+Minute\s+of\s+Round\s+\d+/i)) {
            // Parse round and minute
            const match = trimmedClause.match(/(\d+)(?:st|nd|rd|th)\s+Minute\s+of\s+Round\s+(\d+)/i);
            if (match && result.round && result.timeInRound) {
              const targetMinute = parseInt(match[1], 10);
              const targetRound = parseInt(match[2], 10);
              const [minutes] = result.timeInRound.split(':').map(Number);
              const actualMinute = minutes + 1;
              
              if (result.round === targetRound && actualMinute === targetMinute && !result.wentTheDistance) {
                won = true;
                break;
              }
            }
          }
          // Check for "Last Minute of the 5th Round"
          else if (trimmedClause.match(/Last\s+Minute\s+of\s+(?:the\s+)?5th\s+Round/i)) {
            if (result.round === 5 && result.timeInRound && !result.wentTheDistance) {
              const [minutes] = result.timeInRound.split(':').map(Number);
              if (minutes === 4) { // 5th minute = 4:00-4:59
                won = true;
                break;
              }
            }
          }
        }
      }
      
      if (won) {
        const payout = calculatePayout(bet.amount, bet.odds);
        return { status: 'won', payout };
      }
      return { status: 'lost' };
    }
  }

  // Unknown bet type - mark as lost
  return { status: 'lost' };
}

